class VariantSelects extends HTMLElement {
  constructor () {
    super()
    this.addEventListener('change', this.onVariantChange.bind(this))
    this._postPaintHandle = null
    this._stickyUpdateHandle = null
  }

  onVariantChange () {
    this.updateOptions()
    this.updateCurrentVariant()
    this.updateUI()
  }

  updateOptions () {
    this.options = Array.from(this.querySelectorAll('select'), select => select.value)
  }

  updateCurrentVariant () {
    const variants = this.getVariantData()
    this.currentVariant = variants.find(variant =>
      variant.options.every((option, index) => this.options[index] === option),
    )
  }

  updateUI () {
    this.removeErrorMessage()

    if (!this.currentVariant) {
      this.setUnavailable()
    } else {
      this.updateURL()
      this.updateVariantInput()
      if (!this.usesErpStock()) {
        this.updateAddToCartButtonText()
        this.updateOOSAndBuyButton()
      }
      this.updateShareUrl()
      this.schedulePostPaintUpdates()
    }
  }

  schedulePostPaintUpdates () {
    if (this._postPaintHandle) cancelAnimationFrame(this._postPaintHandle)
    this._postPaintHandle = requestAnimationFrame(() => {
      this._postPaintHandle = null
      this.renderProductInfo()
      this.dispatchVariantChangeEvent()
    })
  }

  scheduleStickyAddToCartUpdate () {
    const stickySection = document.querySelector('product-sticky-add-to-cart')
    if (!stickySection) return

    if (this._stickyUpdateHandle) {
      if (typeof cancelIdleCallback !== 'undefined') {
        cancelIdleCallback(this._stickyUpdateHandle)
      } else {
        clearTimeout(this._stickyUpdateHandle)
      }
      this._stickyUpdateHandle = null
    }

    const runUpdate = () => {
      this._stickyUpdateHandle = null
      this.updateStickyAddToCart()
    }

    if (typeof requestIdleCallback !== 'undefined') {
      this._stickyUpdateHandle = requestIdleCallback(runUpdate, { timeout: 2000 })
    } else {
      this._stickyUpdateHandle = setTimeout(runUpdate, 1)
    }
  }

  updateAddToCartButtonText () {
    const addButton = document.querySelector('[data-product-submit]') || document.querySelector(`[name="add"]`)
    const addButtonText = addButton?.querySelector('span')

    if (addButton && addButtonText) {
      const erpState = this.getErpStockState()
      if (this.isVariantSoldOut(erpState)) {
        addButton.disabled = true
        addButton.classList.add('disabled:opacity-50', 'disabled:pointer-events-none')
        addButtonText.textContent = window.variantStrings.soldOut || 'Sold out'
      } else {
        addButton.disabled = false
        addButton.classList.remove('disabled:opacity-50', 'disabled:pointer-events-none')
        addButtonText.textContent = this.getStockStateButtonText(erpState)
      }
    }
  }

  usesErpStock () {
    return Boolean(document.querySelector('product-stock[data-stock-state]'))
  }

  isVariantSoldOut (erpState) {
    if (this.usesErpStock()) {
      return erpState === 'out-of-stock'
    }
    return !this.currentVariant.available
  }

  getErpStockState () {
    const stockEl = document.querySelector('product-stock[data-stock-state]')
    return stockEl ? stockEl.dataset.stockState : null
  }

  getStockStateButtonText (erpState) {
    if (erpState === 'preorder') return window.variantStrings.preOrder || 'Pre-order'
    if (erpState === 'reserve') return window.variantStrings.reserve || 'Reserve'
    return window.variantStrings.addToCart || 'Add to cart'
  }

  dispatchVariantChangeEvent () {
    const variantChangeEvent = new CustomEvent('variantChange', {
      detail: { variant: this.currentVariant },
      bubbles: true,
    })
    const productImageCarouselEl = document.querySelector('product-media')
    if (productImageCarouselEl) {
      productImageCarouselEl.dispatchEvent(variantChangeEvent)
    } else {
      document.dispatchEvent(variantChangeEvent)
    }
  }

  updateURL () {
    if (this.currentVariant && this.dataset.updateUrl !== 'false') {
      window.history.replaceState({}, '', `${this.dataset.url}?variant=${this.currentVariant.id}`)
    }
  }

  updateVariantInput () {
    const productForms = document.querySelectorAll(`#product-form-${this.dataset.section}, #product-form-installment-${this.dataset.section}`)
    productForms.forEach(productForm => {
      const input = productForm.querySelector('input[name="id"]')
      input.value = this.currentVariant.id
      input.dispatchEvent(new Event('change', { bubbles: true }))
    })
  }

  removeErrorMessage () {
    const section = this.closest('section')
    const productForm = section?.querySelector('product-form')
    productForm?.handleErrorMessage()
  }

  renderProductInfo () {
    this.productInfoElement?.setAttribute('loading', 'true')
    const productPriceElement = document.querySelector('.product-price')
    const productPriceWrapper = productPriceElement?.closest('.product-price__container')
    if (productPriceElement) {
      productPriceElement.setAttribute('loading', 'true')
      productPriceElement.setAttribute('aria-busy', 'true')
    }
    if (productPriceWrapper) {
      productPriceWrapper.setAttribute('data-loading', 'true')
    }

    // Construct URL properly - this.dataset.url already includes the locale prefix
    let productUrl = this.dataset.url
    // Remove leading slash if present
    if (productUrl.startsWith('/')) {
      productUrl = productUrl.slice(1)
    }

    const fetchUrl = `/${productUrl}?variant=${this.currentVariant.id}&section_id=${this.dataset.originalSection || this.dataset.section}`

    fetch(fetchUrl)
      .then(response => response.text())
      .then(responseText => this.updateProductInfo(responseText))
      .catch(() => {
        const productPriceEl = document.querySelector('.product-price')
        const priceWrapper = productPriceEl?.closest('.product-price__container')
        if (productPriceEl) {
          productPriceEl.removeAttribute('loading')
          productPriceEl.removeAttribute('aria-busy')
        }
        if (priceWrapper) {
          priceWrapper.removeAttribute('data-loading')
        }
        this.productInfoElement?.setAttribute('loading', 'false')
      })
      .finally(() => {
        setTimeout(() => {
          this.productInfoElement?.setAttribute('loading', 'false')
          const productPriceEl = document.querySelector('.product-price')
          const priceWrapper = productPriceEl?.closest('.product-price__container')
          const financeOwnsPriceLoading = productPriceEl?.querySelector('finance-module')
          if (productPriceEl && !financeOwnsPriceLoading) {
            productPriceEl.removeAttribute('loading')
            productPriceEl.removeAttribute('aria-busy')
          }
          if (priceWrapper) {
            priceWrapper.removeAttribute('data-loading')
          }
        }, (typeof this.loadingDelay === 'number') ? this.loadingDelay : 100)
      })

    this.scheduleStickyAddToCartUpdate()
  }

  updateStickyAddToCart () {
    const stickySection = document.querySelector('product-sticky-add-to-cart')
    if (!stickySection) return

    const sectionId = stickySection.getAttribute('data-section-id')
    if (!sectionId) return

    // Construct URL for sticky section
    let productUrl = this.dataset.url
    if (productUrl.startsWith('/')) {
      productUrl = productUrl.slice(1)
    }

    const fetchUrl = `/${productUrl}?variant=${this.currentVariant.id}&section_id=${sectionId}`

    fetch(fetchUrl)
      .then(response => response.text())
      .then(responseText => {
        const html = new DOMParser().parseFromString(responseText, 'text/html')
        const newStickySection = html.querySelector('product-sticky-add-to-cart')
        if (newStickySection && stickySection) {
          // Preserve the current visibility state
          const currentClasses = stickySection.className
          const isVisible = stickySection.classList.contains('product-sticky-add-to-cart--show')

          // Update the content
          stickySection.innerHTML = newStickySection.innerHTML

          // Restore the visibility state
          stickySection.className = currentClasses
          if (isVisible) {
            stickySection.classList.add('product-sticky-add-to-cart--show')
          }
        }
      })
      .catch(error => {
        console.warn('Failed to update sticky add to cart:', error)
      })
  }

  updateProductInfo (responseText) {
    const html = new DOMParser().parseFromString(responseText, 'text/html')

    // For new design, we need to update price elements directly
    // since they don't have specific IDs like the old design
    this.updateNewDesignPrice(html)
    this.updateNewDesignStock(html)

    // For backward compatibility, try to update old structure elements if they exist
    this.updateElementInnerHtml(`price-${this.dataset.section}`, html)
    this.updateElementInnerHtml(`availability-info-${this.dataset.section}`, html)
    this.updateElementInnerHtml(`stock-notification-${this.dataset.section}`, html)
    this.updateElementInnerHtml(`sale-tag-${this.dataset.section}`, html)
    this.updateElementInnerHtml(`product-tags-${this.dataset.section}`, html)

    const price = document.getElementById(`price-${this.dataset.section}`)
    price?.classList.remove('visibility-hidden')

    this.updateAddToCartButtonText()
    this.updateOOSAndBuyButton()
  }

  updateNewDesignPrice (html) {
    const currentPriceElement = document.querySelector('.product-price')
    if (!currentPriceElement) return

    const newPriceElement = html.querySelector('.product-price')
    if (!newPriceElement) return

    const currentKlarnaHandler = currentPriceElement.querySelector('klarna-geoip-handler')
    const currentKlarnaPlacement = currentPriceElement.querySelector('klarna-placement')

    if (currentKlarnaHandler && currentKlarnaPlacement) {
      const priceContainer = currentPriceElement.querySelector('.product-price__price-stack')
      const newPriceContainer = newPriceElement.querySelector('.product-price__price-stack')
      if (priceContainer && newPriceContainer) {
        priceContainer.innerHTML = newPriceContainer.innerHTML
      }

      const financeModule = currentPriceElement.querySelector('finance-module')
      const newFinanceModule = newPriceElement.querySelector('finance-module')
      if (financeModule && newFinanceModule) {
        Object.keys(newFinanceModule.dataset).forEach(key => {
          financeModule.dataset[key] = newFinanceModule.dataset[key]
        })
        if (financeModule.updateFinanceFromText) {
          const financeLinkText = financeModule.querySelector('.price__finance-link-text')
          if (financeLinkText) {
            financeModule.loading = true
            financeModule.updateFinanceFromText(financeLinkText)
          }
        }
      }
    } else {
      currentPriceElement.innerHTML = newPriceElement.innerHTML

      const currentFinanceModule = currentPriceElement.querySelector('finance-module, finance-module-old')
      const newFinanceModule = newPriceElement.querySelector('finance-module, finance-module-old')
      if (currentFinanceModule && newFinanceModule) {
        currentFinanceModule.innerHTML = newFinanceModule.innerHTML
        Object.keys(newFinanceModule.dataset).forEach(key => {
          currentFinanceModule.dataset[key] = newFinanceModule.dataset[key]
        })
        if (currentFinanceModule.updateFinanceFromText) {
          const financeLinkText = currentFinanceModule.querySelector('.price__finance-link-text')
          if (financeLinkText) {
            currentFinanceModule.loading = true
            currentFinanceModule.updateFinanceFromText(financeLinkText)
          }
        }
      }
    }

    setTimeout(() => this.refreshKlarnaWidget(), 100)
  }

  refreshKlarnaWidget () {
    const newPrice = this.currentVariant?.price
    if (!newPrice) return

    const klarnaPlacement = document.querySelector('klarna-placement')
    if (klarnaPlacement) {
      klarnaPlacement.setAttribute('data-purchase-amount', newPrice)
    }

    setTimeout(() => {
      if (typeof window.KOSMApp !== 'undefined') {
        try {
          window.KOSMApp.updatePurchaseAmount(newPrice)
          window.KOSMApp.refreshPlacements()
        } catch (e) {}
      }

      if (typeof window.Klarna !== 'undefined' && window.Klarna.OnsiteMessaging) {
        try {
          window.Klarna.OnsiteMessaging.refresh()
        } catch (e) {}
      }

      if (typeof window.KlarnaOnsiteService !== 'undefined') {
        try {
          window.KlarnaOnsiteService.push({ eventName: 'refresh-placements' })
        } catch (e) {}
      }
    }, 300)
  }

  updateNewDesignStock (html) {
    // Find the product-stock element in the current page
    const currentStockElement = document.querySelector('product-stock')
    if (!currentStockElement) return

    // Find the product-stock element in the fetched HTML
    const newStockElement = html.querySelector('product-stock')
    if (!newStockElement) return

    // Update the stock content
    currentStockElement.innerHTML = newStockElement.innerHTML

    // Copy over data attributes that might be needed
    const newDataAttributes = newStockElement.dataset
    Object.keys(newDataAttributes).forEach(key => {
      currentStockElement.dataset[key] = newDataAttributes[key]
    })

    // Copy over any classes that might have changed
    currentStockElement.className = newStockElement.className
  }

  updateElementInnerHtml (elementId, html) {
    const destination = document.getElementById(elementId)
    const source = html.getElementById(elementId)
    if (source && destination) {
      destination.innerHTML = source.innerHTML
    }
  }

  setUnavailable () {
    const addButton = document.querySelector(`[name="add"]`)
    const addButtonText = addButton?.querySelector('span')

    if (addButton && addButtonText) {
      addButton.disabled = true
      addButtonText.textContent = window.variantStrings.unavailable

      // For new design, update the price to show unavailable state
      const priceElement = document.querySelector('.product-price .price__with-currency')
      if (priceElement) {
        priceElement.textContent = window.variantStrings.unavailable || 'Unavailable'
      }

      // For backward compatibility, try to hide price element if it exists
      const price = document.getElementById(`price-${this.dataset.section}`)
      price?.classList.add('visibility-hidden')
    }
  }

  updateShareUrl () {
    const shareButton = document.getElementById(`Share-${this.dataset.section}`)
    if (shareButton?.updateUrl) {
      shareButton.updateUrl(`${window.shopUrl}${this.dataset.url}?variant=${this.currentVariant.id}`)
    }
  }

  getVariantData () {
    if (!this.variantData) {
      this.variantData = JSON.parse(this.querySelector('[type="application/json"]').textContent)
    }
    return this.variantData
  }

  get productInfoElement () {
    // Try to find the new product-body element first, fallback to old structure
    return document.querySelector('.product-body') || document.getElementById(`ProductInfo-${this.dataset.section}`)
  }

  get loadingDelay () {
    return Number(this.dataset.loadingDelay) || 100
  }

  updateOOSAndBuyButton () {
    const oosBlock = document.querySelector('[data-oos-block]')
    const addButton = document.querySelector('[data-product-submit]')
    if (!oosBlock || !addButton) return
    const erpState = this.getErpStockState()
    const isOOS = this.isVariantSoldOut(erpState)
    if (isOOS) {
      oosBlock.classList.remove('hidden')
      addButton.disabled = true
      addButton.classList.add('disabled:opacity-50', 'disabled:pointer-events-none')
      addButton.querySelector('span').textContent = window.variantStrings.soldOut || 'Sold out'
    } else {
      oosBlock.classList.add('hidden')
      addButton.disabled = false
      addButton.classList.remove('disabled:opacity-50', 'disabled:pointer-events-none')
      addButton.querySelector('span').textContent = this.getStockStateButtonText(erpState)
    }
  }
}

customElements.define('variant-selects', VariantSelects)

class VariantRadios extends VariantSelects {
  updateOptions () {
    const fieldsets = Array.from(this.querySelectorAll('fieldset'))
    this.options = fieldsets.map((fieldset) => {
      const checkedRadio = Array.from(fieldset.querySelectorAll('input')).find(
        (radio) => radio.checked,
      )

      return checkedRadio?.value || ''
    })
  }
}

customElements.define('variant-radios', VariantRadios)
